import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthorizedLayout } from "../../components/AuthorizedLayout";
import iconSend from "../../assets/icone-enviar.png";
import iconBack from "../../assets/Tipode material (1).png";
import CheckMessage from '../../components/ChatComponent/CheckMessage';
import moment from "moment";

import "./Chat.css";
import api from "../../utils/api";

const Chat = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isFetching, setIsFetching] = useState(false);
  const [menssages, setMenssages] = useState([]);
  const [message, setMessage] = useState("");
  const [lastFetchTime, setLastFetchTime] = useState(0); // For throttling
  const [previousMessagesLength, setPreviousMessagesLength] = useState(0); // Track message count
  const [isUserAtBottom, setIsUserAtBottom] = useState(true); // Track if user is at bottom
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false); // Track keyboard state
  const [initialViewportHeight, setInitialViewportHeight] = useState(window.innerHeight);
  const [companyName, setCompanyName] = useState(""); // Store company name

    // Detect virtual keyboard open/close
  useEffect(() => {
    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const heightDiff = initialViewportHeight - currentHeight;

      // If height difference is more than 120px, keyboard is likely open (valor menor para detectar mais cedo)
      const keyboardOpen = heightDiff > 120;
      setIsKeyboardOpen(keyboardOpen);

      // For iOS, handle viewport changes
      if (keyboardOpen && /iPhone|iPad|iPod/.test(navigator.userAgent)) {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      }
    };

    const handleVisualViewportChange = () => {
      if (window.visualViewport) {
        const heightDiff = window.innerHeight - window.visualViewport.height;
        setIsKeyboardOpen(heightDiff > 120);
      }
    };

    // Store initial viewport height
    setInitialViewportHeight(window.innerHeight);

    window.addEventListener('resize', handleResize);

    // Use Visual Viewport API if available (better for mobile)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
    }

    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        setInitialViewportHeight(window.innerHeight);
      }, 500);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', () => {});
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
      }
    };
  }, [initialViewportHeight]);

  // Auto-scroll when keyboard opens and user is typing
  useEffect(() => {
    if (isKeyboardOpen) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 300);
    }
  }, [isKeyboardOpen]);


  const fetchMessages = async () => {
    // Throttling: don't make new request if last one was less than 1 second ago
    const now = Date.now();
    if (isFetching || (now - lastFetchTime < 1000)) {
      return;
    }

    setIsFetching(true);
    setLastFetchTime(now);

    try {
      const { data } = await api.get(`/talents/messages/${id}/`);

      const sortedMessages = data?.data.sort(function (a, b) {
        if (a.attributes.createdAt < b.attributes.createdAt) return -1;
        if (a.attributes.createdAt > b.attributes.createdAt) return 1;
        return 0;
      });

      setPreviousMessagesLength(menssages.length);
      setMenssages(sortedMessages);

      // Buscar nome da empresa nas mensagens
      if (sortedMessages && sortedMessages.length > 0) {
        const companyMessage = sortedMessages.find(msg => msg?.attributes?.sender?.type === "Company");
        if (companyMessage?.attributes?.sender?.companyName) {
          setCompanyName(companyMessage.attributes.sender.companyName);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetching(false);
    }
  };

   useEffect(() => {
      fetchMessages();
    }, []);

  useEffect(() => {
    let intervalId = null;

    intervalId = setInterval(fetchMessages, 2000);
    return () => clearInterval(intervalId);
  }, [isFetching]);

  const sendMessage = async () => {
    if (!message.trim()) return;

    // Create optimistic message
    const newMessage = {
      id: `temp-${Date.now()}`,
      attributes: {
        sender: { type: "Talent" },
        textMsg: message,
        createdAt: new Date().toISOString(),
        receiver: menssages[0]?.attributes?.sender
      }
    };

    // Add message optimistically and clear input
    setMenssages(prev => [...prev, newMessage]);
    const messageToSend = message;
    setMessage("");

    // Always scroll to bottom when user sends a message
    setIsUserAtBottom(true);

    try {
      await api.post(`/talents/messages/`, {
        type: "messages",
        data: {
          attributes: {
            receiver_id: menssages[0]?.attributes?.sender?.id,
            receiver_type: "Company",
            text_msg: messageToSend,
          },
        },
      });
    } catch (error) {
      console.log(error);
      // Remove temporary message in case of error
      setMenssages(prev => prev.filter(msg => msg.id !== newMessage.id));
      alert("Something unexpected happened. Please try again.");
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handleInputFocus = () => {
    // Delay to ensure keyboard is fully open
    setTimeout(() => {
      if (isKeyboardOpen) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 500);
  };

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const checkIfUserAtBottom = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const threshold = 100; // 100px threshold
      setIsUserAtBottom(scrollTop + clientHeight >= scrollHeight - threshold);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  };

  const scrollToBottomSmooth = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    // Only scroll to bottom if there are new messages and user was at bottom
    const hasNewMessages = menssages.length > previousMessagesLength;
    if (hasNewMessages && isUserAtBottom) {
      scrollToBottom();
    }
  }, [menssages, previousMessagesLength, isUserAtBottom]);

  return (
    <AuthorizedLayout>
      <div className={`chat-container-inbox ${isKeyboardOpen ? 'keyboard-open' : ''}`}>
        <div
          className="headerBackINbox"
          onClick={() => {
            navigate("/inbox");
          }}
        >
          <img src={iconBack} alt="Voltar" />{" "}
          <strong style={{ color: "#211E1F" }}>{companyName}</strong>
        </div>

        <div className="messagesInbox" ref={messagesContainerRef} onScroll={checkIfUserAtBottom}>
          {menssages?.map((msg, index) => (
            <div
              key={index}
              className={`messageInbox ${
                msg?.attributes?.sender?.type === "Company" ? "bot" : "user"
              }`}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{msg?.attributes?.textMsg}</span>
                <span className="message-time-inbox">
                  <span>{moment(msg?.attributes?.createdAt).format('HH:mm')}</span>
                  {msg?.attributes?.sender?.type === "Talent" && (
                    <CheckMessage readed={msg?.attributes?.readed} />
                  )}
                </span>
              </div>
            </div>
          ))}
           <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom button - only show when user is not at bottom */}
        {!isUserAtBottom && (
          <button
            className="scroll-to-bottom-btn"
            onClick={() => {
              setIsUserAtBottom(true);
              scrollToBottomSmooth();
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 10L12 15L17 10"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        <div className={`input-containers ${isKeyboardOpen ? 'keyboard-active' : ''}`}>
          <input
            type="text"
            placeholder="Mensagem"
            value={message || ""}
            onChange={(event) => setMessage(event.target.value)}
            onKeyDown={handleKeyPress}
            onFocus={handleInputFocus}
            enterKeyHint="send"
            autoComplete="on"
            autoCorrect="on"
            spellCheck="true"
            autoCapitalize="sentences"
          />
          <button onClick={sendMessage}>
            <img src={iconSend} alt="Enviar" />
          </button>
        </div>
      </div>
    </AuthorizedLayout>
  );
};

export default Chat;
